import { Component, DoCheck, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup, ReactiveFormsModule } from '@angular/forms';
import {
  FieldType,
  FieldTypeConfig,
  FormlyFieldConfig,
  FormlyModule,
} from '@ngx-formly/core';
import { MatTableModule } from '@angular/material/table';
import { ApplicationFacade } from '@intellio/shared/state-mgmt';
import {
  IntelligentActionContentResult,
  SitePlanArrayComparison,
  SitePlanArrayDetailsContent,
  SitePlanArrayEvidence,
  SitePlanArrayReviewDraft,
} from '@intellio/shared/models';
import {
  compareIntelligentActionResults,
  sitePlanArrayInstance,
  sitePlanArrayResultState,
  SitePlanArrayResultState,
} from '@intellio/shared/utils';
import { combineLatest, of, Subject, takeUntil } from 'rxjs';

interface ReviewRow {
  comparison: SitePlanArrayComparison;
  form: UntypedFormGroup;
  model: { review: { extractedControl: string; actualControl: string } };
  fields: FormlyFieldConfig[];
}

@Component({
  selector: 'itc-site-plan-array-details',
  templateUrl: './site-plan-array-details.component.html',
  styleUrls: ['./site-plan-array-details.component.scss'],
  imports: [FormlyModule, ReactiveFormsModule, MatTableModule],
})
export class SitePlanArrayDetailsComponent
  extends FieldType<FieldTypeConfig>
  implements OnInit, DoCheck, OnDestroy
{
  supported = false;
  state: SitePlanArrayResultState = 'missing';
  result?: IntelligentActionContentResult;
  content?: SitePlanArrayDetailsContent;
  rows: ReviewRow[] = [];
  readonly unmatchedSitePlanColumns = [
    'array',
    'panels',
    'tilt',
    'azimuth',
    'analysis',
  ];
  private destroyed$ = new Subject<void>();
  private loadedKey?: string;

  constructor(private facade: ApplicationFacade) {
    super();
  }

  get readOnly(): boolean {
    return !!(
      this.props.readonly ||
      this.props.disabled ||
      this.formControl?.disabled
    );
  }

  ngOnInit(): void {
    combineLatest([
      this.facade.currentApplication$,
      this.facade.applicationIntelligentActionResults$,
    ])
      .pipe(takeUntil(this.destroyed$))
      .subscribe(([application, results]) => {
        const instanceId = sitePlanArrayInstance(
          application?.system?.energySourceId
        );
        this.supported = instanceId !== null;
        const result = this.supported
          ? results?.find(
              (item) =>
                item.applicationId === application.id &&
                item.ruleName === 'SitePlanArrayDetails' &&
                item.instanceId === instanceId
            )
          : undefined;
        const key = JSON.stringify([application?.id, instanceId, result]);
        if (key === this.loadedKey) return;
        this.loadedKey = key;
        this.result = result;
        this.state = sitePlanArrayResultState(result);
        this.content = this.state === 'ready' ? result.content : undefined;
        this.props.ruleName = 'SitePlanArrayDetails';
        this.props.instanceId = instanceId;
        this.props.notLoaded = this.state !== 'ready';
        this.props.valuesMatch = false;
        this.rows = [];
        if (!this.content) {
          this.formControl.setValue(null);
          return;
        }
        const draft: SitePlanArrayReviewDraft = {
          applicationId: result.applicationId,
          instanceId,
          values: {},
        };
        this.rows = [...this.content.comparisons]
          .sort((a, b) => a.applicationOrder - b.applicationOrder)
          .map((comparison) => {
            draft.values[comparison.comparisonId] = comparison.currentValue;
            const applicationArrayLabel =
              comparison.applicationArray.label ||
              comparison.applicationArrayId;
            return {
              comparison,
              form: new UntypedFormGroup({}),
              model: {
                review: {
                  extractedControl: comparison.currentValue,
                  actualControl: comparison.actualValue,
                },
              },
              fields: [
                {
                  key: 'review',
                  type: 'intelligent-action-comparison',
                  props: {
                    result$: of({ ...result, content: comparison }),
                    notLoaded: false,
                    intelligentActionName: $localize`Application Array: ${applicationArrayLabel}`,
                    wrapComparison: true,
                    readonly: this.readOnly,
                    comparisonId: comparison.comparisonId,
                    onReviewValueChange: (value: string) =>
                      this.updateDraft(comparison.comparisonId, value),
                  },
                },
              ],
            };
          });
        this.updateMatchState(draft);
        this.formControl.setValue(draft);
      });
  }

  ngDoCheck(): void {
    for (const row of this.rows) row.fields[0].props.readonly = this.readOnly;
  }

  private updateDraft(comparisonId: string, value: string): void {
    if (this.readOnly) return;
    const draft = this.formControl.value as SitePlanArrayReviewDraft;
    const updated = {
      ...draft,
      values: { ...draft.values, [comparisonId]: value },
    };
    this.updateMatchState(updated);
    this.formControl.setValue(updated);
    this.formControl.markAsDirty();
  }

  private updateMatchState(draft: SitePlanArrayReviewDraft): void {
    this.props.valuesMatch =
      this.rows.length > 0 &&
      this.rows.every((row) =>
        compareIntelligentActionResults(
          draft.values[row.comparison.comparisonId],
          row.comparison.actualValue
        )
      );
  }

  evidenceValue(
    evidence: SitePlanArrayEvidence,
    key: 'panelCount' | 'tilt' | 'azimuth'
  ): string {
    return evidence[key] !== null
      ? `${evidence[key]}${key === 'panelCount' ? '' : '°'}`
      : evidence.rawValues[key] || $localize`Unavailable`;
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
